import React from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MoveHorizontal } from 'lucide-react';
import { cn, capitalizeFirst } from '@/lib/utils';

export type MarkdownTextRenderer = (text: string) => React.ReactNode[];

export interface MarkdownTypographyTokens {
  paragraph: string;
  inlineText: string;
  block: string;
  list: string;
  listItem: string;
}

export interface MarkdownTableTokens {
  wrapper: string;
  hint: string;
  scroller: string;
  table: string;
  thead: string;
  tbody: string;
  row: string;
  th: string;
  td: string;
}

export interface MarkdownShellTokens {
  wrapper: string;
  prose: string;
}

export type MarkdownComponentsWithText = Components & {
  text?: ({ children }: { children: React.ReactNode }) => React.ReactNode;
};

interface CreateMarkdownComponentsOptions {
  typography: MarkdownTypographyTokens;
  table: MarkdownTableTokens;
  renderLink?: Components['a'];
  renderTextWithCitations?: MarkdownTextRenderer;
  t?: (key: string) => string;
}

const applyTextRenderer = (
  children: React.ReactNode,
  renderer: (text: string) => React.ReactNode[]
): React.ReactNode => {
  if (typeof children === 'string') {
    const result = renderer(children);
    return result.length === 1 && typeof result[0] === 'string' ? result[0] : <>{result}</>;
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === 'string') {
        const result = renderer(child);
        return result.length === 1 && typeof result[0] === 'string'
          ? result[0]
          : <React.Fragment key={i}>{result}</React.Fragment>;
      }
      return child;
    });
  }
  return children;
};

/**
 * Capitalizes the first letter of text content in React children
 */
const capitalizeChildren = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string') {
    return capitalizeFirst(children);
  }
  if (Array.isArray(children) && children.length > 0) {
    const firstChild = children[0];
    if (typeof firstChild === 'string') {
      return [capitalizeFirst(firstChild), ...children.slice(1)];
    }
  }
  return children;
};

export const createMarkdownComponents = ({
  typography,
  table,
  renderLink,
  renderTextWithCitations,
  t,
}: CreateMarkdownComponentsOptions): MarkdownComponentsWithText => ({
  p: ({ children, className, ...props }) => {
    const processed = renderTextWithCitations
      ? applyTextRenderer(children, renderTextWithCitations)
      : children;
    return (
      <p className={cn(typography.paragraph, className)} {...props}>
        {capitalizeChildren(processed)}
      </p>
    );
  },
  ul: ({ children, className, ...props }) => (
    <ul className={cn(typography.list, className)} {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, className, ...props }) => (
    <ol className={cn(typography.list, className)} {...props}>
      {children}
    </ol>
  ),
  a: renderLink ?? (({ children, className, ...props }) => (
    <a className={cn('underline', className)} {...props}>
      {children}
    </a>
  )),
  span: ({ children, className, ...props }) => (
    <span className={cn(typography.inlineText, className)} {...props}>
      {children}
    </span>
  ),
  div: ({ children, className, ...props }) => (
    <div className={cn(typography.block, className)} {...props}>
      {children}
    </div>
  ),
  li: ({ children, className, ...props }) => {
    const processed = renderTextWithCitations
      ? applyTextRenderer(children, renderTextWithCitations)
      : children;
    return (
      <li className={cn(typography.listItem, className)} {...props}>
        {capitalizeChildren(processed)}
      </li>
    );
  },
  blockquote: ({ children, className, ...props }) => (
    <blockquote
      className={cn(
        'my-4 border-l-2 border-neutral-300 dark:border-neutral-700 pl-4 text-neutral-500 dark:text-neutral-400',
        className
      )}
      {...props}
    >
      {children}
    </blockquote>
  ),
  table: ({ children, className, ...props }) => {
    const scrollerRef = React.useRef<HTMLDivElement | null>(null);
    const [isScrollable, setIsScrollable] = React.useState(false);

    React.useEffect(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      const updateScrollable = () => {
        const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
        setIsScrollable(hasOverflow);
      };

      updateScrollable();

      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', updateScrollable);
        return () => window.removeEventListener('resize', updateScrollable);
      }

      const observer = new ResizeObserver(updateScrollable);
      observer.observe(scroller);
      const nativeTable = scroller.querySelector('table');
      if (nativeTable) {
        observer.observe(nativeTable);
      }

      return () => observer.disconnect();
    }, [children]);

    return (
      <div className={table.wrapper}>
        {isScrollable && (
          <div className={table.hint}>
            <MoveHorizontal className="w-3 h-3 mr-1" />
            {t ? t('common.scroll_to_view') : 'Scroll to view'}
          </div>
        )}
        <div ref={scrollerRef} className={table.scroller}>
          <table className={cn(table.table, className)} {...props}>
            {children}
          </table>
        </div>
      </div>
    );
  },
  thead: ({ children, className, ...props }) => (
    <thead className={cn(table.thead, className)} {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, className, ...props }) => (
    <tbody className={cn(table.tbody, className)} {...props}>
      {children}
    </tbody>
  ),
  tr: ({ children, className, ...props }) => (
    <tr className={cn(table.row, className)} {...props}>
      {children}
    </tr>
  ),
  th: ({ children, className, ...props }) => (
    <th className={cn(table.th, className)} {...props}>
      {children}
    </th>
  ),
  td: ({ children, className, ...props }) => (
    <td className={cn(table.td, className)} {...props}>
      {children}
    </td>
  ),
  text: ({ children }: { children: React.ReactNode }) => {
    if (!renderTextWithCitations) {
      return <>{children}</>;
    }

    const content = typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.join('')
        : String(children ?? '');

    return <>{renderTextWithCitations(content)}</>;
  },
});

interface MarkdownRendererProps {
  content: string;
  components: MarkdownComponentsWithText;
  shell: MarkdownShellTokens;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  components,
  shell,
}) => (
  <div className={shell.wrapper}>
    <ReactMarkdown
      className={shell.prose}
      components={components}
      remarkPlugins={[remarkGfm]}
    >
      {content}
    </ReactMarkdown>
  </div>
);
